export class ThemeToggle {
  private isDark = false;
  private readonly storageKey = 'theme-preference';
  private toggleButton: HTMLButtonElement | null = null;

  constructor() {
    this.toggleButton = document.getElementById('theme-toggle') as HTMLButtonElement | null;
    this.loadTheme();
    this.listenToSystemThemeChanges();
    this.attachEvents();
  }

  private getStoredPreference(): 'light' | 'dark' | null {
    try {
      const preference = localStorage.getItem(this.storageKey);
      if (preference === 'light' || preference === 'dark') {
        return preference;
      }
      return null;
    } catch {
      return null;
    }
  }

  private loadTheme(): void {
    const storedPreference = this.getStoredPreference();
    if (storedPreference) {
      this.isDark = storedPreference === 'dark';
    } else {
      this.isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    this.applyTheme();
  }

  private listenToSystemThemeChanges(): void {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    mediaQuery.addEventListener('change', (event) => {
      if (this.getStoredPreference()) {
        return;
      }
      this.isDark = event.matches;
      this.applyTheme();
    });
  }

  private attachEvents(): void {
    this.toggleButton?.addEventListener('click', () => {
      this.isDark = !this.isDark;
      this.applyTheme();
      this.persistPreference();
    });
  }

  private persistPreference(): void {
    try {
      localStorage.setItem(this.storageKey, this.isDark ? 'dark' : 'light');
    } catch {
      // Silently ignore storage errors (private / incognito modes)
    }
  }

  private applyTheme(): void {
    document.documentElement.classList.toggle('dark', this.isDark);
    this.updateButtonState();
  }

  private updateButtonState(): void {
    if (!this.toggleButton) return;

    const label = this.isDark ? 'Activer le thème clair' : 'Activer le thème sombre';
    this.toggleButton.setAttribute('aria-label', label);
    this.toggleButton.setAttribute('aria-pressed', String(this.isDark));
    this.toggleButton.dataset.theme = this.isDark ? 'dark' : 'light';
  }
}
